import { useState, useEffect } from 'react';
import Button from '../base/Button';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [logoAnimation, setLogoAnimation] = useState(0);

  // Logo animation effect
  useEffect(() => {
    const animationInterval = setInterval(() => {
      setLogoAnimation(prev => prev + 1);
    }, 4000);

    return () => clearInterval(animationInterval);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d3p3loevn82kpvfp39e0', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickLinks = [
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    { name: 'EV Retrofitting', href: '#services' },
    { name: 'Battery Packs', href: '#services' },
    { name: 'Fleet Management', href: '#services' },
    { name: 'Charging Stations', href: '#services' },
    { name: 'Maintenance', href: '#services' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Stay Updated with EV Innovation
            </h3>
            <p className="text-orange-100 mb-8 max-w-2xl mx-auto">
              Get the latest news on electric vehicle conversion technology, sustainability tips, and exclusive offers.
            </p>
            <form 
              onSubmit={handleNewsletterSubmit}
              className="max-w-md mx-auto flex gap-4"
              data-readdy-form
              id="newsletter-form"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <Button
                type="submit"
                variant="secondary"
                size="md"
                loading={isSubmitting}
                disabled={!email.trim()}
              >
                Subscribe
              </Button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-blue-100 mt-4">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-200 mt-4">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Enhanced Logo */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-4 mb-6 group">
              {/* Enhanced Logo with Transparent Background */}
              <div className="relative">
                {/* Floating Energy Particles */}
                <div className="absolute -inset-6 pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className={`absolute w-1 h-1 bg-orange-400 rounded-full animate-ping opacity-40 ${
                        logoAnimation % 2 === 0 ? 'animate-bounce' : 'animate-pulse'
                      }`}
                      style={{
                        left: `${15 + i * 12}%`,
                        top: `${25 + (i % 4) * 18}%`,
                        animationDelay: `${i * 0.4}s`,
                        animationDuration: `${2.5 + i * 0.3}s`
                      }}
                    />
                  ))}
                </div>

                {/* Animated Glow Rings */}
                <div className="absolute inset-0 w-16 h-16 rounded-2xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-amber-400 rounded-2xl opacity-15 animate-ping" />
                  <div className="absolute inset-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-xl opacity-25 animate-pulse" />
                  <div className="absolute inset-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl opacity-35 animate-bounce" />
                  <div className="absolute inset-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg opacity-45 animate-spin" style={{ animationDuration: '8s' }} />
                </div>

                {/* Main Logo Container */}
                <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center transform group-hover:scale-115 transition-all duration-700 group-hover:rotate-3 overflow-hidden">
                  {/* Transparent Background with Subtle Border */}
                  <div className="absolute inset-0 bg-transparent border-2 border-orange-400/20 rounded-2xl group-hover:border-orange-500/40 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-orange-500/30" />
                  
                  {/* Logo Image with Transparent Background */}
                  <div className="relative w-14 h-14 flex items-center justify-center">
                    <img 
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/197b98e8a3590d2360a3b866f8f2dcb4.png" 
                      alt="DJT PowerTech Logo" 
                      className="w-12 h-12 object-contain transform group-hover:scale-110 transition-all duration-500 group-hover:brightness-110"
                      style={{ 
                        filter: 'drop-shadow(0 3px 6px rgba(251, 146, 60, 0.4)) brightness(1.1) contrast(1.1)',
                        background: 'transparent',
                        mixBlendMode: 'normal'
                      }}
                    />
                    
                    {/* Animated Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer rounded-2xl" />
                  </div>
                  
                  {/* Energy Pulse Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400/15 via-amber-400/15 to-orange-400/15 rounded-2xl opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-500" />
                </div>
                
                {/* Live Status Indicator */}
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse border border-gray-900 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-ping" />
                </div>
                
                {/* EV Badge - Moved to avoid overlap */}
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs px-2 py-1 rounded-full font-black shadow-xl animate-pulse border border-white/20">
                    ⚡ EV
                  </div>
                </div>
              </div>

              {/* Enhanced Company Text */}
              <div className="relative">
                {/* Animated Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/5 to-amber-400/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg" />
                
                <div className="relative">
                  {/* Main Company Name */}
                  <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-orange-200 to-amber-200 group-hover:from-orange-300 group-hover:via-amber-300 group-hover:to-orange-200 transition-all duration-700 tracking-tight uppercase">
                    DJT POWERTECH
                  </h3>
                  
                  {/* Animated Underline */}
                  <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left mb-2 rounded-full" />
                  
                  {/* Subtitle with Animation */}
                  <div className="flex items-center space-x-3">
                    <p className="text-sm font-bold text-orange-300 group-hover:text-orange-200 transition-colors duration-500 tracking-wide">
                      PVT LIMITED
                    </p>
                    <div className="flex space-x-1">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="w-1 h-1 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                      ))}
                    </div>
                    <p className="text-xs font-black text-amber-400 group-hover:text-amber-300 transition-colors duration-500 tracking-widest uppercase">
                      ⚡ EV RETROFITTING
                    </p>
                  </div>
                  
                  {/* Floating Sparkles */}
                  <div className="absolute -inset-2 pointer-events-none">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 bg-amber-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping"
                        style={{
                          left: `${i * 30}%`,
                          top: `${i % 2 === 0 ? '15%' : '85%'}`,
                          animationDelay: `${i * 0.3}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6 max-w-md">
              Leading the electric mobility revolution in India and beyond. We convert petrol vehicles to electric, 
              helping businesses save costs while contributing to a sustainable future through advanced retrofitting technology.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/djthaika/" target="_blank" rel="noopener noreferrer" 
                 className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a href="https://www.facebook.com/djthaika/" target="_blank" rel="noopener noreferrer"
                 className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-colors">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="https://www.instagram.com/djthaika/" target="_blank" rel="noopener noreferrer"
                 className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-5 transition-colors">
                <i className="ri-instagram-line text-xl"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-gray-300 hover:text-blue-400 transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <a href={service.href} className="text-gray-300 hover:text-blue-400 transition-colors">
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-phone-line text-blue-400"></i>
              </div>
              <div>
                <a href="tel:18005700229" className="text-gray-300 hover:text-blue-400">
                  1-800-570-0229
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-mail-line text-blue-400"></i>
              </div>
              <div>
                <a href="mailto:info@djthaika.com" className="text-gray-300 hover:text-blue-400">
                  info@djthaika.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="text-center pt-8 border-t border-gray-200">
          <p className="text-gray-600">
            © 2024 DJT PowerTech Retrofitting. All rights reserved.
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        .animate-shimmer { 
          animation: shimmer 2.5s ease-in-out infinite; 
        }
      `}</style>
    </footer>
  );
}
